import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  StackDivider,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Editor } from "@tiptap/react";
import {
  RiAlignLeft,
  RiBold,
  RiEdit2Line,
  RiHeading,
  RiInformationLine,
  RiItalic,
  RiLink,
  RiListOrdered,
  RiListUnordered,
  RiMore2Line,
  RiParagraph,
  RiStrikethrough,
  RiSubscript,
  RiSuperscript,
  RiUnderline,
  RiArrowDownSLine,
  RiArrowUpSLine,
} from "react-icons/ri";
import { DropdownIcon } from "../../../../constants/commonIcons";
import { RichTextButton } from "./RichTextButton";
import RichTextShortcutsModal from "./RichTextShortcutsModal";
import RichTextColorPicker from "./RichTextColorPicker";
import RichTextInsertTable from "./RichTextInsertTable";
import RichTextTablePopover from "./RichTextTablePopover";
import RichTextLinkModal from "./RichTextLinkModal";

type Props = {
  editor: Editor | null;
  isDisabled?: boolean;
};

const iconSize = "18";
const buttonColorScheme = "gray";
const buttonVariant = "ghost";

const headingLevelToLabelAndIcon = (
  level: number | undefined,
): [React.ReactElement, string] => {
  if (level === undefined)
    return [<RiParagraph size={iconSize} />, "Normal text"];
  return [<RiHeading size={iconSize} />, `Heading ${level}`];
};

export function RichTextToolbar({ editor, isDisabled }: Props) {
  const openShortcutsModal = useDisclosure({ defaultIsOpen: false });
  const linkModal = useDisclosure();
  const expandedToolbar = useDisclosure();

  const isMobile = useBreakpointValue({ base: true, lg: false });
  const buttonSize = useBreakpointValue({ base: "sm", lg: "md" });

  if (!editor) return null;

  const [textSizeIcon, textSizeLabel] = headingLevelToLabelAndIcon(
    editor.getAttributes("heading")?.level,
  );

  const handleOpenShortcutsModal = () => {
    openShortcutsModal.onOpen();
  };

  const handleCloseShortcutsModal = () => {
    openShortcutsModal.onClose();
  };

  if (isMobile) {
    return (
      <chakra-scope>
        <Box pl="3" pr="3" m="auto" maxW="container.lg">
          <VStack spacing="2" py="3">
            <HStack w="full" justify="space-between" spacing="2">
              <Menu closeOnSelect={false} size="sm">
                {({ onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      size="sm"
                      leftIcon={textSizeIcon}
                      rightIcon={<Icon as={DropdownIcon} />}
                      variant="ghost"
                      colorScheme="gray"
                      fontSize="sm"
                      minW="20"
                      bg="white"
                    />
                    <MenuList>
                      <MenuItem
                        bg={
                          editor.isActive("paragraph") ? "brand.50" : undefined
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          onClose();
                          editor.chain().focus().setParagraph().run();
                          setTimeout(() => editor.commands.focus(), 0);
                        }}>
                        Normal text
                      </MenuItem>
                      <MenuItem
                        bg={
                          editor.isActive("heading", { level: 1 })
                            ? "brand.50"
                            : undefined
                        }
                        onClick={() => {
                          onClose();
                          editor
                            .chain()
                            .focus()
                            .setHeading({ level: 1 })
                            .focus()
                            .run();
                          setTimeout(() => editor.commands.focus(), 0);
                        }}>
                        Heading 1
                      </MenuItem>
                      <MenuItem
                        bg={
                          editor.isActive("heading", { level: 2 })
                            ? "brand.50"
                            : undefined
                        }
                        onClick={() => {
                          onClose();
                          editor
                            .chain()
                            .focus()
                            .setHeading({ level: 2 })
                            .focus()
                            .run();
                          setTimeout(() => editor.commands.focus(), 0);
                        }}>
                        Heading 2
                      </MenuItem>
                      <MenuItem
                        bg={
                          editor.isActive("heading", { level: 3 })
                            ? "brand.50"
                            : undefined
                        }
                        onClick={() => {
                          onClose();
                          editor
                            .chain()
                            .focus()
                            .setHeading({ level: 3 })
                            .focus()
                            .run();
                          setTimeout(() => editor.commands.focus(), 0);
                        }}>
                        Heading 3
                      </MenuItem>
                      <MenuItem
                        bg={
                          editor.isActive("heading", { level: 4 })
                            ? "brand.50"
                            : undefined
                        }
                        onClick={() => {
                          onClose();
                          editor
                            .chain()
                            .focus()
                            .setHeading({ level: 4 })
                            .focus()
                            .run();
                          setTimeout(() => editor.commands.focus(), 0);
                        }}>
                        Heading 4
                      </MenuItem>
                    </MenuList>
                  </>
                )}
              </Menu>

              <ButtonGroup
                variant="ghost"
                colorScheme="gray"
                size="sm"
                spacing="1"
                bg="white">
                <RichTextButton
                  isDisabled={
                    isDisabled ||
                    !editor.can().chain().focus().toggleBold().run()
                  }
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  isActive={editor.isActive("bold")}
                  label="Bold">
                  <RiBold size="14" />
                </RichTextButton>
                <RichTextButton
                  isDisabled={
                    isDisabled ||
                    !editor.can().chain().focus().toggleItalic().run()
                  }
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  isActive={editor.isActive("italic")}
                  label="Italic">
                  <RiItalic size="14" />
                </RichTextButton>
                <RichTextButton
                  isDisabled={
                    isDisabled ||
                    !editor.can().chain().focus().toggleUnderline().run()
                  }
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  isActive={editor.isActive("underline")}
                  label="Underline">
                  <RiUnderline size="14" />
                </RichTextButton>
              </ButtonGroup>

              <ButtonGroup
                variant="ghost"
                colorScheme="gray"
                size="sm"
                spacing="1"
                bg="white">
                <RichTextButton
                  isDisabled={
                    isDisabled ||
                    !editor.can().chain().focus().toggleBulletList().run()
                  }
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  isActive={editor.isActive("bulletList")}
                  label="Bullet List">
                  <RiListUnordered size="14" />
                </RichTextButton>
                <RichTextButton
                  isDisabled={
                    isDisabled ||
                    !editor.can().chain().focus().toggleOrderedList().run()
                  }
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  isActive={editor.isActive("orderedList")}
                  label="Ordered List">
                  <RiListOrdered size="14" />
                </RichTextButton>
              </ButtonGroup>

              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="gray"
                bg="white"
                icon={
                  expandedToolbar.isOpen ? (
                    <RiArrowUpSLine size="14" />
                  ) : (
                    <RiArrowDownSLine size="14" />
                  )
                }
                onClick={expandedToolbar.onToggle}
                aria-label={
                  expandedToolbar.isOpen
                    ? "Hide advanced tools"
                    : "Show more tools"
                }
              />
            </HStack>

            <Collapse
              in={expandedToolbar.isOpen}
              animateOpacity
              style={{ width: "full" }}>
              <HStack justify="space-between" spacing="2">
                <ButtonGroup
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  spacing="1"
                  bg="white">
                  <RichTextButton
                    isDisabled={
                      isDisabled ||
                      !editor.can().chain().focus().toggleStrike().run()
                    }
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive("strike")}
                    label="Strike">
                    <RiStrikethrough size="14" />
                  </RichTextButton>
                  <RichTextButton
                    isDisabled={
                      isDisabled ||
                      !editor.can().chain().focus().toggleHighlight().run()
                    }
                    onClick={() =>
                      editor.chain().focus().toggleHighlight().run()
                    }
                    isActive={editor.isActive("highlight")}
                    label="Highlight">
                    <RiEdit2Line size="14" />
                  </RichTextButton>
                </ButtonGroup>

                <Box bg="white">
                  <RichTextColorPicker
                    editor={editor}
                    iconSize="14"
                    buttonColorScheme="gray"
                    buttonVariant="ghost"
                  />
                </Box>

                <ButtonGroup
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  spacing="1"
                  bg="white">
                  <RichTextButton
                    isDisabled={
                      isDisabled ||
                      !editor.can().chain().focus().toggleSubscript().run()
                    }
                    onClick={() =>
                      editor.chain().focus().toggleSubscript().run()
                    }
                    isActive={editor.isActive("subscript")}
                    label="Subscript">
                    <RiSubscript size="14" />
                  </RichTextButton>
                  <RichTextButton
                    isDisabled={
                      isDisabled ||
                      !editor.can().chain().focus().toggleSuperscript().run()
                    }
                    onClick={() =>
                      editor.chain().focus().toggleSuperscript().run()
                    }
                    isActive={editor.isActive("superscript")}
                    label="Superscript">
                    <RiSuperscript size="14" />
                  </RichTextButton>
                </ButtonGroup>

                <Box bg="white">
                  <RichTextInsertTable
                    editor={editor}
                    rows={2}
                    columns={2}
                    iconSize="14"
                    buttonColorScheme="gray"
                    buttonVariant="ghost"
                  />
                </Box>

                <ButtonGroup
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  bg="white">
                  <RichTextButton
                    isDisabled={isDisabled}
                    onClick={linkModal.onOpen}
                    isActive={editor.isActive("link")}
                    label={editor.isActive("link") ? "Edit Link" : "Add Link"}>
                    <RiLink size="14" />
                  </RichTextButton>
                </ButtonGroup>
              </HStack>
            </Collapse>
          </VStack>
        </Box>
        <RichTextTablePopover
          editor={editor}
          buttonVariant="ghost"
          buttonColorScheme="gray"
        />
        <RichTextShortcutsModal
          isOpen={openShortcutsModal.isOpen}
          onClose={handleCloseShortcutsModal}
        />
        <RichTextLinkModal
          isOpen={linkModal.isOpen}
          onClose={linkModal.onClose}
          editor={editor}
        />
      </chakra-scope>
    );
  }

  // Desktop toolbar (original layout)
  return (
    <chakra-scope>
      <Box pl="2" m="auto" maxW="container.lg">
        <HStack
          py="0.5"
          gap="2"
          divider={<StackDivider mt="3px !important" mb="3px !important" />}>
          <Menu closeOnSelect={false} size="sm">
            {({ onClose }) => (
              <>
                <MenuButton
                  as={Button}
                  minW="40"
                  leftIcon={textSizeIcon}
                  rightIcon={<Icon as={DropdownIcon} />}
                  variant={buttonVariant}
                  colorScheme={buttonColorScheme}
                  size="sm"
                  fontSize="sm">
                  {textSizeLabel}
                </MenuButton>
                <MenuList>
                  <MenuItem
                    bg={editor.isActive("paragraph") ? "brand.50" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      editor.chain().focus().setParagraph().run();
                      setTimeout(() => editor.commands.focus(), 0);
                    }}>
                    Normal text
                  </MenuItem>
                  <MenuItem
                    bg={
                      editor.isActive("heading", { level: 1 })
                        ? "brand.50"
                        : undefined
                    }
                    onClick={() => {
                      onClose();
                      editor
                        .chain()
                        .focus()
                        .setHeading({ level: 1 })
                        .focus()
                        .run();
                      setTimeout(() => editor.commands.focus(), 0);
                    }}>
                    Heading 1
                  </MenuItem>
                  <MenuItem
                    bg={
                      editor.isActive("heading", { level: 2 })
                        ? "brand.50"
                        : undefined
                    }
                    onClick={() => {
                      onClose();
                      editor
                        .chain()
                        .focus()
                        .setHeading({ level: 2 })
                        .focus()
                        .run();
                      setTimeout(() => editor.commands.focus(), 0);
                    }}>
                    Heading 2
                  </MenuItem>
                  <MenuItem
                    bg={
                      editor.isActive("heading", { level: 3 })
                        ? "brand.50"
                        : undefined
                    }
                    onClick={() => {
                      onClose();
                      editor
                        .chain()
                        .focus()
                        .setHeading({ level: 3 })
                        .focus()
                        .run();
                      setTimeout(() => editor.commands.focus(), 0);
                    }}>
                    Heading 3
                  </MenuItem>
                  <MenuItem
                    bg={
                      editor.isActive("heading", { level: 4 })
                        ? "brand.50"
                        : undefined
                    }
                    onClick={() => {
                      onClose();
                      editor
                        .chain()
                        .focus()
                        .setHeading({ level: 4 })
                        .focus()
                        .run();
                      setTimeout(() => editor.commands.focus(), 0);
                    }}>
                    Heading 4
                  </MenuItem>
                </MenuList>
              </>
            )}
          </Menu>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}
            spacing="0">
            <RichTextButton
              isDisabled={
                isDisabled || !editor.can().chain().focus().toggleBold().run()
              }
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive("bold")}
              label="Bold">
              <RiBold size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled || !editor.can().chain().focus().toggleItalic().run()
              }
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive("italic")}
              label="Italic">
              <RiItalic size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleUnderline().run()
              }
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive("underline")}
              label="Underline">
              <RiUnderline size={iconSize} />
            </RichTextButton>
            <RichTextInsertTable
              editor={editor}
              rows={2}
              columns={2}
              iconSize={iconSize}
              buttonColorScheme={buttonColorScheme}
              buttonVariant={buttonVariant}
            />
          </ButtonGroup>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}
            spacing="0">
            <RichTextButton
              isDisabled={
                isDisabled || !editor.can().chain().focus().toggleStrike().run()
              }
              onClick={() => editor.chain().focus().toggleStrike().run()}
              label="Strike">
              <RiStrikethrough size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleHighlight().run()
              }
              onClick={() => editor.chain().focus().toggleHighlight().run()}
              label="Highlight">
              <RiEdit2Line size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleSubscript().run()
              }
              onClick={() => editor.chain().focus().toggleSubscript().run()}
              isActive={editor.isActive("subscript")}
              label="Subscript">
              <RiSubscript size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleSuperscript().run()
              }
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              isActive={editor.isActive("superscript")}
              label="Superscript">
              <RiSuperscript size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={isDisabled}
              onClick={linkModal.onOpen}
              isActive={editor.isActive("link")}
              label={editor.isActive("link") ? "Edit Link" : "Add Link"}>
              <RiLink size={iconSize} />
            </RichTextButton>
          </ButtonGroup>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}
            spacing="0">
            <RichTextColorPicker
              editor={editor}
              iconSize={iconSize}
              buttonColorScheme={buttonColorScheme}
              buttonVariant={buttonVariant}
            />
          </ButtonGroup>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}
            spacing="0">
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().unsetTextAlign().run()
              }
              isActive={editor.isActive({ textAlign: "left" })}
              onClick={() => editor.chain().focus().unsetTextAlign().run()}
              label="Align Left">
              <RiAlignLeft size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().setTextAlign("center").run()
              }
              isActive={editor.isActive({ textAlign: "center" })}
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }
              label="Align Center">
              <RiAlignLeft size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().setTextAlign("right").run()
              }
              isActive={editor.isActive({ textAlign: "right" })}
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              label="Align Right">
              <RiAlignLeft size={iconSize} />
            </RichTextButton>
          </ButtonGroup>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}
            spacing="0">
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleBulletList().run()
              }
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive("bulletList")}
              label="Bullet List">
              <RiListUnordered size={iconSize} />
            </RichTextButton>
            <RichTextButton
              isDisabled={
                isDisabled ||
                !editor.can().chain().focus().toggleOrderedList().run()
              }
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive("orderedList")}
              label="Ordered List">
              <RiListOrdered size={iconSize} />
            </RichTextButton>
          </ButtonGroup>

          <ButtonGroup
            variant={buttonVariant}
            colorScheme={buttonColorScheme}
            size={buttonSize}>
            <RichTextButton
              onClick={handleOpenShortcutsModal}
              label="Keyboard Shortcuts">
              <RiInformationLine size={iconSize} />
            </RichTextButton>
          </ButtonGroup>
        </HStack>
      </Box>
      <RichTextTablePopover
        editor={editor}
        buttonVariant={buttonVariant}
        buttonColorScheme={buttonColorScheme}
      />
      <RichTextShortcutsModal
        isOpen={openShortcutsModal.isOpen}
        onClose={handleCloseShortcutsModal}
      />
      <RichTextLinkModal
        isOpen={linkModal.isOpen}
        onClose={linkModal.onClose}
        editor={editor}
      />
    </chakra-scope>
  );
}
